import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import { SessionApiError } from '../../session/client';
import { code128c, qrModules } from './card-barcode';
import {
  type RewardsCard as Card,
  type ReplaceReason,
  appleWalletLink,
  canUseAppleWallet,
  getCard,
  googleWalletLink,
  isGoogleSaveUrl,
  replaceCard,
} from './rewards-api';

/**
 * The Just Treated Rewards Card on the Rewards page (stage W2).
 *
 * The server's card, drawn: the QR (payload `JT1:<number>`), a Code 128 of the
 * number for older counter scanners, and the number printed for typing in. It
 * carries no balance and nothing about the person — no name, no pharmacy, no
 * purchase (DPIA addendum §3). "Show at the counter" opens the card full screen
 * on white, the widest quiet zone the screen allows, and keeps the screen awake
 * where the browser can. "Lost or damaged?" replaces it: the old number stops
 * working at once and for good.
 *
 * Absent while the platform flag `penny.card` is off: the card route answers
 * 404 and nothing is drawn, not even a heading.
 */

function messageOf(error: unknown): string {
  return error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.';
}

function useEscape(onEscape: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onEscape();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onEscape]);
}

export function QrCode({
  text,
  size,
  label,
}: {
  readonly text: string;
  readonly size: number;
  readonly label: string;
}): JSX.Element {
  const qr = qrModules(text);
  const quiet = 4;
  const n = qr.size + quiet * 2;
  const cells: JSX.Element[] = [];
  for (let row = 0; row < qr.size; row += 1)
    for (let col = 0; col < qr.size; col += 1)
      if (qr.dark(row, col))
        cells.push(<rect key={`${row}-${col}`} x={col + quiet} y={row + quiet} width={1.02} height={1.02} />);
  return (
    <svg
      className="jt-rc-qr"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${n} ${n}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
    >
      <rect width={n} height={n} fill="#fff" />
      <g fill="#000">{cells}</g>
    </svg>
  );
}

export function Code128({
  digits,
  height,
  label,
}: {
  readonly digits: string;
  readonly height: number;
  readonly label: string;
}): JSX.Element {
  const code = code128c(digits);
  return (
    <svg
      className="jt-rc-bars"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${code.modules} 40`}
      preserveAspectRatio="none"
      height={height}
      shapeRendering="crispEdges"
    >
      <rect width={code.modules} height={40} fill="#fff" />
      <g fill="#000">
        {code.bars.map((bar) => (
          <rect key={bar.x} x={bar.x} y={0} width={bar.w} height={40} />
        ))}
      </g>
    </svg>
  );
}

const REASONS: readonly { readonly id: ReplaceReason; readonly label: string }[] = [
  { id: 'LOST', label: 'I’ve lost my phone or the card' },
  { id: 'STOLEN', label: 'Someone else has my card number' },
  { id: 'DAMAGED', label: 'It won’t scan' },
];

export function RewardsCardPanel({
  flash,
}: {
  readonly flash: (message: string) => void;
}): JSX.Element | null {
  const [state, setState] = useState<{ card?: Card; off?: boolean; error?: unknown }>({});
  const [full, setFull] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const load = useCallback(() => {
    getCard()
      .then((card) => setState(card === null ? { off: true } : { card }))
      .catch((error: unknown) => setState({ error }));
  }, []);
  useEffect(load, [load]);

  if (state.off === true) return null;
  if (state.card === undefined && state.error === undefined) return null; // loading: nothing jumps in later than the page
  if (state.card === undefined)
    return (
      <section aria-label="Rewards Card" className="jt-rw-card">
        <span className="jt-rw-h2">Your Rewards Card</span>
        <span className="jt-rw-copy" role="alert">
          We couldn’t load your card. {messageOf(state.error)}
        </span>
        <button type="button" className="jt-ord-pill jt-ord-pill--soft jt-rw-pill" onClick={load}>
          Try again
        </button>
      </section>
    );
  const card = state.card;
  const suspended = card.status === 'SUSPENDED';
  return (
    <section aria-label="Rewards Card" className="jt-rw-card jt-rc">
      <span className="jt-rw-head">
        <span className="jt-rw-h2">Your Rewards Card</span>
      </span>
      <span className="jt-rw-copy">
        Show it at a Just Treated pharmacy counter. It’s your PENNY account, not money: it holds no balance of
        its own.
      </span>
      {suspended ? (
        <p className="jt-rw-terms jt-rw-terms--test" role="note">
          This card is paused by Just Treated and won’t work at a counter until it is reinstated. Your PENNY
          is safe.
        </p>
      ) : null}
      <div className="jt-rc-face">
        <span className="jt-rc-brand" aria-hidden="true">
          Just Treated <span className="jt-rc-brand__unit">Rewards</span>
        </span>
        <QrCode text={card.payload} size={168} label={`QR code for card ${card.display}`} />
        <Code128 digits={card.number} height={56} label={`Barcode for card ${card.display}`} />
        <span className="jt-rc-number" aria-label={`Card number ${card.display}`}>
          {card.display}
        </span>
      </div>
      <span className="jt-rc-actions">
        <button
          type="button"
          className="jt-ord-pill jt-rc-show"
          onClick={() => setFull(true)}
          disabled={suspended}
        >
          Show at the counter
        </button>
        {card.wallets?.apple === true && !suspended && canUseAppleWallet(navigator.userAgent) ? (
          <AddToAppleWallet onFailure={(message) => flash(message)} />
        ) : null}
        {card.wallets?.google === true && !suspended && !canUseAppleWallet(navigator.userAgent) ? (
          <AddToGoogleWallet onFailure={(message) => flash(message)} />
        ) : null}
        <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={() => setReplacing(true)}>
          Lost or damaged?
        </button>
      </span>
      {full ? <FullCard card={card} onClose={() => setFull(false)} /> : null}
      {replacing ? (
        <ReplaceDialog
          card={card}
          onClose={() => setReplacing(false)}
          onReplaced={(next) => {
            setReplacing(false);
            setState({ card: next });
            flash('Your new card is ready. The old number no longer works.');
          }}
        />
      ) : null}
    </section>
  );
}

/** Asks for a short-lived link to the person's own pass, then opens it: Safari hands a .pkpass to Wallet. */
function AddToAppleWallet({ onFailure }: { readonly onFailure: (message: string) => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="jt-ord-pill jt-rc-apple"
      disabled={busy}
      aria-busy={busy}
      onClick={() => {
        setBusy(true);
        appleWalletLink()
          .then((link) => window.location.assign(link.downloadPath))
          .catch((error: unknown) =>
            onFailure(`The card could not be added to Apple Wallet. ${messageOf(error)}`),
          )
          .finally(() => setBusy(false));
      }}
    >
      {busy ? 'Opening Apple Wallet…' : 'Add to Apple Wallet'}
    </button>
  );
}

/** Asks for the person's own "Save to Google Wallet" link, then opens Google's save page. */
function AddToGoogleWallet({ onFailure }: { readonly onFailure: (message: string) => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="jt-ord-pill jt-rc-google"
      disabled={busy}
      aria-busy={busy}
      onClick={() => {
        setBusy(true);
        googleWalletLink()
          .then((link) => {
            if (!isGoogleSaveUrl(link.saveUrl)) throw new Error('Please try again.');
            window.location.assign(link.saveUrl);
          })
          .catch((error: unknown) =>
            onFailure(`The card could not be added to Google Wallet. ${messageOf(error)}`),
          )
          .finally(() => setBusy(false));
      }}
    >
      {busy ? 'Opening Google Wallet…' : 'Add to Google Wallet'}
    </button>
  );
}

function FullCard({ card, onClose }: { readonly card: Card; readonly onClose: () => void }): JSX.Element {
  useEscape(onClose);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
    // Keep the screen on while the card is shown, where the browser allows it. Screen brightness is not
    // something a web page may set: the app does that.
    let lock: { release: () => Promise<void> } | undefined;
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    nav.wakeLock?.request('screen').then(
      (held) => (lock = held),
      () => undefined,
    );
    return () => {
      void lock?.release().catch(() => undefined);
    };
  }, []);
  return (
    <div className="jt-rc-full" role="dialog" aria-modal="true" aria-label="Your Rewards Card, ready to scan">
      <span className="jt-rc-brand jt-rc-brand--full" aria-hidden="true">
        Just Treated <span className="jt-rc-brand__unit">Rewards</span>
      </span>
      <QrCode text={card.payload} size={280} label={`QR code for card ${card.display}`} />
      <Code128 digits={card.number} height={96} label={`Barcode for card ${card.display}`} />
      <span className="jt-rc-number jt-rc-number--full">{card.display}</span>
      <span className="jt-rc-hint">Turn your screen brightness up if the scanner struggles.</span>
      <button ref={close} type="button" className="jt-ord-pill" onClick={onClose}>
        Done
      </button>
    </div>
  );
}

function ReplaceDialog({
  card,
  onClose,
  onReplaced,
}: {
  readonly card: Card;
  readonly onClose: () => void;
  readonly onReplaced: (next: Card) => void;
}): JSX.Element {
  useEscape(onClose);
  const [reason, setReason] = useState<ReplaceReason>('LOST');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);
  const go = (): void => {
    if (busy) return;
    setBusy(true);
    setFailure(undefined);
    replaceCard(card.id, reason, card.version)
      .then((result) => onReplaced(result.card))
      .catch((error: unknown) => {
        setFailure(messageOf(error));
        setBusy(false);
      });
  };
  return (
    <>
      <div className="jt-ord-scrim" onClick={onClose} aria-hidden="true" />
      <div className="jt-ord-modal" role="dialog" aria-modal="true" aria-labelledby="jt-rc-m-title">
        <div className="jt-ord-modal__head">
          <span className="jt-ord-modal__text">
            <span id="jt-rc-m-title" className="jt-ord-modal__title">
              Replace your card?
            </span>
            <span className="jt-ord-modal__copy">
              You’ll get a new card number straight away. The old number stops working for good. Your PENNY
              stays exactly as it is.
            </span>
          </span>
        </div>
        <fieldset className="jt-rc-reasons">
          <legend className="jt-rw-h3">What happened?</legend>
          {REASONS.map((r, i) => (
            <label key={r.id} className="jt-rc-reason">
              <input
                ref={i === 0 ? first : undefined}
                type="radio"
                name="jt-rc-reason"
                value={r.id}
                checked={reason === r.id}
                onChange={() => setReason(r.id)}
              />
              {r.label}
            </label>
          ))}
        </fieldset>
        {failure !== undefined ? (
          <span className="jt-ord-err" role="alert">
            {failure}
          </span>
        ) : null}
        <div className="jt-ord-modal__actions">
          <button type="button" className="jt-ord-pill jt-ord-pill--soft" onClick={onClose}>
            Keep this card
          </button>
          <button type="button" className="jt-ord-pill" onClick={go} disabled={busy} aria-busy={busy}>
            {busy ? 'Replacing…' : 'Replace card'}
          </button>
        </div>
      </div>
    </>
  );
}
